import firebase from 'firebase';
import { db, auth } from '../db';
import Achievements from "./Achievements";
import Notifications from "./Notifications";
import { normalizeEmail, validateEmail, validatePassword } from "../util/validation";
import API_URL from "../api/config.js";
import Events from "./Events";
import { TESTER, DEVELOPER, ADMIN } from "../util/eventTypes";

const SELF_ASSIGNABLE_ROLES = ["tester", "developer"];

function roleCode(userType) {
  if (userType === "developer") return DEVELOPER;
  if (userType === "tester") return TESTER;
  if (userType === "admin") return ADMIN;
  return null;
}

// helper Function
function err(e) {
  return { success: false, error: e };
}

const emailVerifiedPageHTML = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Email Verified</title>
  <style>
    body {
      margin: 0;
      padding: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100vh;
      font-family: 'Arial', sans-serif;
      background: linear-gradient(to bottom, orange, white, grey);
    }
    .container {
      text-align: center;
      max-width: 600px;
      background: white;
      padding: 20px;
      border-radius: 10px;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }
    h1 { color: orange; font-size: 2.5em; margin-bottom: 20px; }
    p { color: grey; font-size: 1.2em; margin: 10px 0; }
    .loading { color: #666; font-style: italic; }
  </style>
</head>
<body>
  <div class="container">
    <h1>Your Email Has Been Verified</h1>
    <p class="loading">Signing you in...</p>
    <script>
      setTimeout(() => {
        window.location.href = '/login?verified=true';
      }, 3000);
    </script>
  </div>
</body>
</html>
`;

function handleEmailVerification() {
  const queryParams = new URLSearchParams(window.location.search);
  const mode = queryParams.get('mode');
  const oobCode = queryParams.get('oobCode');

  if (mode === 'verifyEmail' && oobCode) {
    auth.applyActionCode(oobCode)
      .then(() => {
        console.log('✅ Email verified successfully.');
        showEmailVerifiedPage();
      })
      .catch((error) => {
        console.error('❌ Error verifying email:', error);
        alert('Email verification failed. Please try again or contact support.');
      });
  }
}

function showEmailVerifiedPage() {
  document.open();
  document.write(emailVerifiedPageHTML);
  document.close();
}

async function setDefaultSettings(uid){
  try{
    const userDoc = await db.collection("User").doc(uid).get();
    if(!userDoc.exists)return { success: false, message: "User not found, something went wrong" };
    console.log(userDoc.data().userType);
    if(userDoc.data().userType === "tester"){
      await userDoc.ref.set({settings:
        {
          notifications:{playtestNotification: true, surveyNotification: true, discoverPlaytestNotification: true},
        }
      }, { merge: true }); //  Prevents erasing existing data in this document
      console.log("settings set to default");
    }else if(userDoc.data().userType === "developer"){
      await userDoc.ref.set({settings:
        {
          notifications:{playtestNotification: true, surveyNotification: true},
        }
      }, { merge: true }); //  Prevents erasing existing data in this document
      console.log("settings set to default");
    }

  }catch(err){
     throw new Error("Failed to set default settings", { cause: err });
  }
  
}

async function registerUser(userDetails) {
  const {
    name,
    userType,
    email,
    password,
    img,
    nickName,
    gender,
    bio,
    platform,
    behavior,
    genre,
    profile,
    purchasingBehavior
  } = userDetails;

  const normalizedEmail = normalizeEmail(email);

  const emailError = validateEmail(normalizedEmail);
  if (emailError) return { success: false, error: emailError };

  const passwordError = validatePassword(password);
  if (passwordError) return { success: false, error: passwordError };

  if (SELF_ASSIGNABLE_ROLES.indexOf(userType) === -1) {
    return { success: false, error: "Choose either a tester or a developer account" };
  }

  try {
    const userCredential = await auth.createUserWithEmailAndPassword(normalizedEmail, password);
    const authUserId = userCredential.user.uid;
    console.log("✅ User created successfully in Firebase Auth:", authUserId);

    await db.collection("User").doc(authUserId).set({
      email: normalizedEmail,
      img: img || "",
      userName: name || "",
      userType: userType,
      nickName: nickName || "No nickname provided",
      gender: gender || "Gender not specified",
      bio: bio || "No bio provided",
      lastSeen: new Date(Date.now() - 86400000),
      emailVerified: false,
    });

    if (userType === "tester") {
      await db.collection("PlayTester").doc(authUserId).set({
        age: "1",
        platform: platform || "",
        behavior: behavior || "",
        gender: gender || "",
        genre: genre || "",
        hours: "0",
        profile: profile || "",
        purchasingBehavior: purchasingBehavior || "",
        testerID: authUserId,
        loginStreak: 0,
        completeProfile: false,
        surveysCompleted: 0,
        bestStreak: 0,
        achievementsCompleted: 0,
        projectsJoined: [],
        firstTimeUser: [1, 1, 1, 1],
      });
    }
    await setDefaultSettings(authUserId);
    Events.logEvent("account.created", { userType: roleCode(userType) });

    Notifications.createRoleNotification(
      2,
      "user-register",
      "New account registered",
      "",
      {
        userName: name || "No name",
        userEmail: normalizedEmail,
        userRole: userType || "unknown",
        registeredUserId: authUserId,
      }
    ).catch(e => console.log(e));

    await auth.currentUser.sendEmailVerification({
      url: `${window.location.origin}/login`,
      handleCodeInApp: false
    });
    console.log("📧 Verification email sent to:", normalizedEmail);

    await auth.signOut();

    return {
      success: true,
      message: "Please verify your account through the email we sent you.",
      redirect: "/login",
      data: { id: authUserId, userType },
    };
  } catch (error) {
    console.error("❌ Error during user registration:", error);
    return { success: false, error: error.message };
  }
}

async function loginUser(userDetails) {
  const normalizedEmail = normalizeEmail(userDetails.email);
  const password = userDetails.password;

  return new Promise((resolve, reject) => {
    const emailError = validateEmail(normalizedEmail);
    if (emailError) {
      reject({ success: false, error: emailError });
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      reject({ success: false, error: passwordError });
      return;
    }

    const signIn = () => auth.signInWithEmailAndPassword(normalizedEmail, password)
      .then(userCred => {
        if (!userCred.user.emailVerified) {
          auth.signOut()
            .then(() => reject({ success: false, error: "Please verify your email address before signing in." }))
            .catch(e => reject({ success: false, error: e }));
          return;
        }

        db.collection("User").where("email", "==", normalizedEmail).get()
          .then(querySnapshot => {
            if (querySnapshot.empty) {
              auth.signOut().then(() => {
                localStorage.clear();
                reject(err("unidentified email"));
              }).catch(e => reject({ success: false, error: e }));
              return;
            }

            querySnapshot.forEach(async doc => {
              if (!doc.data().emailVerified) {
                await db.collection("User").doc(doc.id).update({ emailVerified: true });
              }
              Events.logEvent("auth.login", { userType: roleCode(doc.data().userType) });
              await handleUserLogin(doc, resolve, reject);
            });
          })
          .catch(e => reject({ success: false, error: e }));
      })
      .catch(e => {
        localStorage.clear();
        reject({ success: false, error: e.message });
      });

    if (auth.currentUser) {
      logoutUser()
        .then(() => signIn())
        .catch(e => reject({ success: false, error: e }));
      return;
    }

    signIn();
  });
}

async function handleUserLogin(doc, resolve, reject) {
  try {
    const type = doc.data().userType;
    let isTester = type === "tester" ? 1 : 0;
    let isDev = type === "developer" ? 1 : 0;

    const lastSeenField = doc.data().lastSeen;
    const oldLastSeen = lastSeenField ? lastSeenField.toDate() : new Date();

    const newDate = new Date();

    await db.collection("User").doc(doc.id).update({ lastSeen: newDate });

    if (isTester) {
      const playTesterDoc = await db.collection("PlayTester").doc(doc.id).get();
      if (playTesterDoc.exists) {
        let testerData = playTesterDoc.data();
        let differenceDays = newDate.setHours(0, 0, 0, 0) - oldLastSeen.setHours(0, 0, 0, 0);

        if (!testerData.loginStreak || !testerData.bestStreak) {
          await db.collection("PlayTester").doc(doc.id).update({ loginStreak: 1, bestStreak: 1 });
        } else if (differenceDays > 100000) {
          await db.collection("PlayTester").doc(doc.id).update({
            loginStreak: firebase.firestore.FieldValue.increment(1),
            bestStreak: testerData.bestStreak
          });
        }
      }
    }

    resolve({
      success: true,
      data: {
        id: doc.id,
        userType: doc.data().userType,
        email: doc.data().email,
        img: doc.data().img,
      }
    });

  } catch (error) {
    reject({ success: false, error: error.message });
  }
}

async function logoutUser() {
  return new Promise((resolve, reject) => {
    if (!auth.currentUser) {
      reject({ success: false, error: "no user logged in" });
    }

    auth.signOut()
      .then(() => {
        localStorage.clear();
        resolve({ success: true });
      })
      .catch(e => reject({ success: false, error: e }));
  });
}

async function updateProfile(userId, userName, nickName, gender, bio, email, workphone, mobile, img) {
  return new Promise((resolve, reject) => {
    db.collection("User").doc(userId).get()
      .then(doc => {
        if (!doc.exists) reject({ success: false, error: "user doesn't exist" });

        db.collection("User").doc(userId).update({
          userId: userId || "",
          userName: userName || "",
          nickName: nickName || "",
          gender: gender || "",
          bio: bio || "",
          email: email ? email.toLowerCase() : "",
          workPhone: workphone || "",
          mobile: mobile || "",
          img: img || ""
        })
          .then(() => resolve({ success: true }))
          .catch(e => reject(err(e)));
      })
      .catch(e => reject(err(e)));
  });
}

async function getProfile(userId) {
  return new Promise((resolve, reject) => {
    db.collection("User").doc(userId).get()
      .then(doc => {
        if (!doc.exists) reject({ success: false, error: "no user found with given user id" });
        db.collection("PlayTester").doc(doc.id).get()
          .then(new_doc => {
            resolve({
              success: true,
              data: {
                userName: doc.data().userName,
                nickName: doc.data().nickName,
                img: doc.data().img,
                email: doc.data().email,
                workPhone: doc.data().workPhone,
                mobile: doc.data().mobile,
                gender: doc.data().gender,
                bio: doc.data().bio,
                achievementsCompleted: new_doc.data().achievementsCompleted,
              }
            });
          })
          .catch(e => reject(err(e)));
      })
      .catch(e => reject(err(e)));
  });
}

export async function deleteUser(userId) {
  if (!userId) {
    return { success: false, error: "No user ID provided" };
  }

  try {
    const doc = await db.collection("User").doc(userId).get();
    if (!doc.exists) {
      return { success: false, error: "user doesn't exist" };
    }

    if (!auth.currentUser) {
      return { success: false, error: "You must be signed in to delete a user." };
    }

    let token;
    try {
      token = await auth.currentUser.getIdToken();
    } catch (e) {
      return { success: false, error: "Your session has expired. Please sign in again." };
    }

    //Request deleting a user
    let response;
    try {
      response = await fetch(
        `${API_URL}/delete-user/${userId}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {
      return {
        success: false,
        error: "Could not reach the account server, so the user was not deleted."
      };
    }

    let data = {};
    try {
      data = await response.json();
    } catch (e) {
      data = {};
    }

    if (!response.ok || !data.success) {
      return {
        success: false,
        error: data.error || `Account server returned ${response.status}`
      };
    }

    //this.getUsers()//we may need to refresh user list
    await db.collection("User").doc(userId).delete();
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
}

export default {
  registerUser,
  loginUser,
  logoutUser,
  updateProfile,
  getProfile,
  deleteUser,
  handleEmailVerification,
  setDefaultSettings,
};



// WEBPACK FOOTER //
// ./src/Firestore/UserObject.js